import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  ProgressBar,
  Spinner,
  Table,
} from "react-bootstrap";

const money = (value) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(
    Number(value || 0),
  );
const date = (value) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match
    ? `${match[3]}/${match[2]}/${match[1]}`
    : new Date(value).toLocaleDateString("es-AR");
};
const pct = (value) =>
  `${Number(value || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const status = (value) => {
  const normalized = String(value || "PENDING").toUpperCase();
  if (normalized === "COMPLETE") return ["Completo", "success"];
  if (normalized === "EMPTY_CONFIRMED") return ["Vacío", "secondary"];
  if (normalized === "RUNNING") return ["Procesando", "info"];
  if (normalized === "PENDING") return ["Pendiente", "warning"];
  return ["Error", "danger"];
};

export default function ConciliacionOperacionesDaily({
  darkMode,
  fechaLiquidacion,
  setFechaLiquidacion,
  archivoZip,
  setArchivoZip,
  analizando,
  handleAnalizarZip,
  operationalRun,
  resultadoConciliacion,
  reportProgress,
  matchingBusy,
  matchingPhase,
  ejecutarMatch,
  resolverReview,
  confirmTerminalMapping,
  consultarCloverNuevosMappings,
  buscarCloverMerchants,
  retryingFailedReports,
  retryFailedReports,
  historialConciliacion,
  cargandoHistorial,
  cargarHistorialConciliacion,
  abrirRunConciliacion,
  mappingSummary,
  debit,
  openMappingModal,
  mappingModal,
  setMappingModal,
  mappingCandidates,
  mappingBusy,
  saveMapping,
  mappingQuery,
  setMappingQuery,
  searchMappingCandidates,
  unlinkMapping,
}) {
  const [reportsOpen, setReportsOpen] = useState(true);
  const [tab, setTab] = useState("MATCH");
  const [selectedResult, setSelectedResult] = useState(null);
  const [manualMapping, setManualMapping] = useState(null);
  const [merchantQuery, setMerchantQuery] = useState("");
  const [merchantCandidates, setMerchantCandidates] = useState([]);
  const reports =
    reportProgress?.reports || operationalRun?.clover?.reports || [];
  const total = Number(
    reportProgress?.totalReports ?? reportProgress?.total ?? 0,
  );
  const complete = Number(reportProgress?.complete || 0);
  const empty = Number(reportProgress?.empty || 0);
  const consulted = Number(reportProgress?.consulted ?? complete + empty);
  const running = Number(reportProgress?.running || 0);
  const failed = Number(reportProgress?.failed || 0);
  // `pending` already comes from the run summary. Adding the difference against
  // the detail array counted the same reports twice whenever details were not loaded.
  const pending = Number(reportProgress?.pending || 0);
  const closed = Boolean(reportProgress?.terminal);
  const hasFailures = reportProgress?.hasFailures ?? failed > 0;
  const matchInputError = reportProgress?.matchInputError;
  const finalReady = Boolean(resultadoConciliacion);
  const reportPercent = total
    ? Math.min(100, ((consulted + failed) / total) * 100)
    : 0;
  const summary = resultadoConciliacion?.resumen || {};
  const comparable = Number(
    summary.operacionesComparables ??
      summary.fiservComparable ??
      summary.operacionesFiserv ??
      0,
  );
  const matched = Number(summary.conciliadas ?? summary.match ?? 0);
  const autoMatched = Number(summary.autoMatched ?? matched);
  const manualMatched = Number(summary.manualMatched ?? 0);
  const rate =
    resultadoConciliacion?.run?.matchAutomaticoPorcentaje ??
    (comparable ? (matched / comparable) * 100 : 0);
  const resultCategory = (row) => {
    const value = String(row.estadoActual || row.status || "").toUpperCase();
    return value === "MATCH"
      ? "MATCH"
      : value === "REVIEW" || value === "AMBIGUOUS_REVIEW"
        ? "REVIEW"
        : value === "SOLO_FISERV" || value === "MISSING"
          ? "MISSING"
        : value === "SOLO_CLOVER"
          ? "SOLO_CLOVER"
          : "DIFERENCIA";
  };
  const resultRows = useMemo(
    () =>
      (resultadoConciliacion?.resultados || []).filter(
        (row) => resultCategory(row) === tab,
      ),
    [resultadoConciliacion, tab],
  );
  const mappingItems = useMemo(() => Object.values((mappingSummary?.items || []).reduce((groups, item) => {
    const current = groups[item.terminalFiserv] || { ...item, marcas: [], cantidadOperacionesFiserv: 0, importeFiserv: 0 };
    current.cantidadOperacionesFiserv += Number(item.cantidadOperacionesFiserv || 0);
    current.importeFiserv += Number(item.importeFiserv || 0);
    current.marcas = [...new Set([...(current.marcas || []), ...(item.marcas || [])])];
    if (!current.cloverCandidate && item.cloverCandidate) current.cloverCandidate = item.cloverCandidate;
    current.confirmedDurably = Boolean(current.confirmedDurably || item.confirmedDurably);
    groups[item.terminalFiserv] = current;
    return groups;
  }, {})), [mappingSummary]);
  const stepState = (index) => {
    if (index < 2) return "complete";
    if (index === 2) return closed && !hasFailures ? "complete" : "active";
    if (index === 3)
      return matchingBusy ? "active" : finalReady ? "complete" : "pending";
    return finalReady ? "complete" : "pending";
  };
  const steps = [
    "Procesando Fiserv",
    "Identificando comercios",
    "Consultando Clover",
    "Validando operaciones",
    "Conciliación final",
  ];

  return (
    <section
      className={
        darkMode
          ? "container-dark daily-reconciliation-page"
          : "container-light bg-gris daily-reconciliation-page"
      }
    >
      <div className="container py-4">
        <div className="daily-heading">
          <div>
            <div className="daily-eyebrow">OPERACIÓN DIARIA</div>
            <h1>Conciliación de Operaciones</h1>
            <p>
              Validación diaria de liquidaciones Fiserv contra operaciones
              Clover.
            </p>
          </div>
          {finalReady ? (
            <Badge
              pill
              bg={matched === comparable ? "success" : "warning"}
              className="daily-status"
            >
              {matched === comparable
                ? "LISTO PARA LIQUIDAR"
                : "REQUIERE REVISIÓN"}
            </Badge>
          ) : null}
        </div>
        <Card className="daily-card">
          <Card.Body>
            <div className="daily-card-heading">
              <div>
                <h2>Comenzar conciliación</h2>
                <p>
                  Seleccioná la fecha de liquidación y el archivo Fiserv para
                  iniciar.
                </p>
              </div>
              <span className="daily-step-number">01</span>
            </div>
            <Form onSubmit={handleAnalizarZip}>
              <div className="daily-form-grid">
                <Form.Group>
                  <Form.Label>
                    Fecha de liquidación <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaLiquidacion}
                    onChange={(event) =>
                      setFechaLiquidacion(event.target.value)
                    }
                  />
                  <Form.Text>
                    Debe coincidir con la fecha del SAS que vas a conciliar.
                  </Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Archivo Fiserv</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".zip"
                    onChange={(event) =>
                      setArchivoZip(event.target.files?.[0] || null)
                    }
                  />
                  <Form.Text>
                    {archivoZip?.name || "Formato ZIP de liquidaciones"}
                  </Form.Text>
                </Form.Group>
                <Button
                  type="submit"
                  variant="success"
                  className="daily-primary-action"
                  disabled={!archivoZip || analizando}
                >
                  {analizando ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Procesando...
                    </>
                  ) : (
                    "Conciliar operaciones"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
        {!operationalRun && !resultadoConciliacion ? (
          <div className="daily-empty">
            <div className="daily-empty-icon">↑</div>
            <strong>Seleccioná una liquidación Fiserv para comenzar.</strong>
            <span>
              El proceso consultará y validará las operaciones automáticamente.
            </span>
          </div>
        ) : null}
        {operationalRun && !finalReady ? (
          <Card className="daily-card daily-progress-card">
            <Card.Body>
              <div className="daily-card-heading">
                <div>
                  <h2>Procesando conciliación</h2>
                  <p>
                    {matchingBusy
                      ? "Validando las operaciones del lote Clover cerrado."
                      : "Consultando Clover: el progreso se actualiza automáticamente."}
                  </p>
                </div>
                <Badge
                  bg={
                    hasFailures ? "danger" : matchingBusy ? "primary" : "info"
                  }
                >
                  {hasFailures
                    ? "REQUIERE REINTENTO"
                    : matchingBusy
                      ? "VALIDANDO"
                      : "EN PROCESO"}
                </Badge>
              </div>
              <div className="daily-stepper">
                {steps.map((label, index) => {
                  const current = stepState(index);
                  return (
                    <div className={`daily-step is-${current}`} key={label}>
                      <span>{current === "complete" ? "✓" : index + 1}</span>
                      <div>
                        {label}
                        <small>
                          {current === "complete"
                            ? "Completo"
                            : current === "active"
                              ? "En proceso"
                              : "Pendiente"}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
              <ProgressBar
                now={
                  closed && !hasFailures
                    ? 80
                    : Math.max(20, reportPercent * 0.6)
                }
                className="daily-progress"
              />
              <div className="daily-report-heading">
                <div>
                  <div className="daily-eyebrow">CLOVER</div>
                  <h3>
                    {complete} / {total} completados
                  </h3>
                  <span>
                    Reports totales: {total} · Completos: {complete} ·
                    Procesando: {running} · Pendientes: {pending} · Fallidos:{" "}
                    {failed}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setReportsOpen((open) => !open)}
                >
                  {reportsOpen ? "Ocultar detalle" : "Ver detalle"}
                </Button>
              </div>
              <ProgressBar
                now={reportPercent}
                variant={hasFailures ? "danger" : "success"}
                className="daily-report-progress"
              />
              {hasFailures ? (
                <Alert variant="warning" className="daily-retry-alert">
                  <div>
                    <strong>
                      {failed}{" "}
                      {failed === 1
                        ? "comercio requiere"
                        : "comercios requieren"}{" "}
                      reintento
                    </strong>
                    <span>
                      Los reports completos se conservan; se reenvían únicamente
                      los que fallaron.
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={retryFailedReports}
                    disabled={retryingFailedReports}
                  >
                    {retryingFailedReports ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Reintentando...
                      </>
                    ) : (
                      "Reintentar fallidos"
                    )}
                  </Button>
                </Alert>
              ) : null}
              {reportsOpen ? (
                <div className="daily-table-wrap daily-report-table">
                  <Table responsive hover className="daily-table">
                    <thead>
                      <tr>
                        <th>Comercio</th>
                        <th>Terminal</th>
                        <th>Fecha</th>
                        <th>Cuenta</th>
                        <th>Estado</th>
                        <th>Pagos</th>
                        <th>Enriquecidos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length ? (
                        reports.map((report, index) => {
                          const [label, variant] = status(report.status);
                          const enriched = Number(report.enrichedRows || 0);
                          const partial = Number(
                            report.partialEnrichedRows || 0,
                          );
                          return (
                            <tr
                              key={`${report.runId || "report"}-${report.terminalFiserv}-${report.merchantId}-${index}`}
                            >
                              <td>
                                {report.commerceName ||
                                  report.merchantId ||
                                  "-"}
                              </td>
                              <td>{report.terminalFiserv || "-"}</td>
                              <td>{date(report.date)}</td>
                              <td>{report.accountKey || "-"}</td>
                              <td>
                                <Badge bg={variant}>{label}</Badge>
                                {report.error ? (
                                  <small className="daily-report-error">
                                    {report.error}
                                  </small>
                                ) : null}
                              </td>
                              <td>{report.uiPaymentsCount ?? "-"}</td>
                              <td>
                                {enriched}
                                {partial
                                  ? ` + ${partial} parcial${partial === 1 ? "" : "es"}`
                                  : ""}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="daily-table-empty">
                            Registrando los reports Clover...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : null}
              <div className="daily-report-summary">
                <div>
                  <div className="daily-eyebrow">CLOVER</div>
                  <h3>
                    {consulted} / {total} consultados
                  </h3>
                  <span>
                    Total: {total} · Con datos: {complete} · Vacíos: {empty} ·
                    Errores: {failed} · Procesando: {running} · Pendientes:{" "}
                    {pending}
                  </span>
                  {matchingBusy ? (
                    <small className="d-block mt-1 text-muted">
                      {matchingPhase || "Validando operaciones..."} · Cargando transacciones Clover y ejecutando Matcher V2
                    </small>
                  ) : null}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setReportsOpen((open) => !open)}
                    disabled={matchingBusy}
                  >
                    {reportsOpen ? "Ocultar detalle" : "Ver detalle"}
                  </Button>
                  {closed && !hasFailures ? (
                    <Button
                      size="sm"
                      variant="success"
                      onClick={ejecutarMatch}
                      disabled={matchingBusy}
                    >
                      {matchingBusy ? <><Spinner animation="border" size="sm" className="me-1" />Validando operaciones...</> : "Validar operaciones"}
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : null}
        {finalReady ? (
          <>
            <Card className="daily-card">
              <Card.Body>
                <div className="daily-card-heading">
                  <div>
                    <div className="daily-eyebrow">
                      RESULTADO DE CONCILIACIÓN
                    </div>
                    <h2>Resumen de la liquidación</h2>
                    <p>
                      Liquidación del{" "}
                      {date(
                        fechaLiquidacion ||
                          resultadoConciliacion.run?.fechaLiquidacion,
                      )}
                    </p>
                  </div>
                  <span className="daily-run-label">
                    Conciliación #
                    {String(
                      resultadoConciliacion.run?.id ||
                        operationalRun?.reconciliationRunId ||
                        "",
                    ).slice(-6)}
                  </span>
                </div>
                <div className="daily-match-hero">
                  <div>
                    <div className="daily-match-value">{pct(rate)}</div>
                    <strong>Conciliadas automáticamente</strong>
                    <ProgressBar
                      now={Math.min(Number(rate), 100)}
                      variant="success"
                    />
                    <small>
                      {matched} de {comparable} operaciones comparables
                    </small>
                    <small>Auto: {autoMatched} · Manual: {manualMatched} · Total conciliado: {matched}</small>
                  </div>
                  <div className="daily-summary-grid">
                    {[
                      ["Operaciones Fiserv", summary.operacionesFiserv],
                      ["Operaciones comparables", comparable],
                      [
                        "Requieren revisión",
                        summary.matchProbable,
                      ],
                      [
                        "Sin encontrar",
                        summary.soloFiserv,
                      ],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <small>{label}</small>
                        <strong>{value ?? 0}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </Card.Body>
            </Card>
            <Card className="daily-card">
              <Card.Body>
                {resultadoConciliacion?.families?.length ? <div className="daily-table-wrap mb-3"><h5>Diagnóstico por familia</h5><Table size="sm"><thead><tr><th>Familia</th><th>Comparables</th><th>Match</th><th>Review</th><th>Missing</th><th>Match rate</th></tr></thead><tbody>{resultadoConciliacion.families.map((family) => <tr key={family.family}><td>{family.family}</td><td>{family.comparable}</td><td>{family.match}</td><td>{family.review}</td><td>{family.missing}</td><td>{pct(family.matchRate)}</td></tr>)}</tbody></Table></div> : null}
                <div className="daily-tabs">
                  {[
                    ["MATCH", "Conciliadas", summary.conciliadas ?? matched],
                    [
                      "REVIEW",
                      "Revisar",
                      summary.matchProbable,
                    ],
                    [
                      "MISSING",
                      "Sin encontrar",
                      summary.soloFiserv,
                    ],
                  ].map(([key, label, count]) => (
                    <button
                      type="button"
                      className={tab === key ? "active" : ""}
                      onClick={() => setTab(key)}
                      key={key}
                    >
                      {label}
                      <span>{count ?? 0}</span>
                    </button>
                  ))}
                </div>
                <div className="daily-table-wrap">
                  <Table responsive hover className="daily-table">
                    <thead>
                      <tr>
                        {[
                          "Terminal",
                          "Fecha",
                          "Importe",
                          "Estado",
                          "Regla",
                          "PaymentId",
                        ].map((label) => (
                          <th key={label}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultRows.length ? (
                        resultRows.map((row, index) => (
                          <tr key={row.id || index}>
                            <td>
                              {row.fiserv?.terminal ||
                                row.fiserv?.terminalFiserv ||
                                "-"}
                            </td>
                            <td>
                              {date(
                                row.fiserv?.fechaOperacion ||
                                  row.fiserv?.operationDate,
                              )}
                            </td>
                            <td>
                              {money(
                                row.fiserv?.totalBruto ?? row.fiserv?.amount,
                              )}
                            </td>
                            <td>
                              <Badge
                                bg={
                                  (row.estadoActual || row.status) === "MATCH"
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {row.estadoActual || row.status || "-"}
                              </Badge>
                            </td>
                            <td>{row.rule || "-"}</td>
                            <td>
                              {row.cloverOperacionId || row.clover?.id || "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="daily-table-empty">
                            No hay operaciones para esta vista.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}
        {mappingSummary ? (
          <Card className="daily-card daily-mapping-card">
            <Card.Body>
              <div>
                <div className="daily-eyebrow">CONFIGURACIÓN</div>
                <h2>Comercios identificados</h2>
                <p>El mapping se administra por separado del flujo diario.</p>
              </div>
              <div className="daily-mapping-number">
                {mappingSummary.suggested || 0}{" "}
                <span>/ {mappingSummary.total || 0}</span>
                <small className="d-block text-muted">identificados (sugeridos)</small>
              </div>
              <div className="daily-mapping-footer">
                <span>
                  Pendientes: <strong>{mappingSummary.pending || 0}</strong>
                  <span className="ms-3">Guardados globalmente: <strong>{mappingSummary.confirmed || 0}</strong></span>
                </span>
                <Button
                  variant="outline-primary"
                  onClick={() => openMappingModal(mappingSummary.item)}
                >
                  Administrar mappings
                </Button>
              </div>
              {mappingItems.length ? (
                <div className="daily-table-wrap mt-3">
                  <Table size="sm" responsive>
                    <thead><tr><th>Terminal</th><th>Familia</th><th>Operaciones</th><th>Importe</th><th>Estado</th><th>Merchant candidato</th><th>Comercio</th><th>Cuenta</th><th>Confianza</th><th /></tr></thead>
                    <tbody>{mappingItems.map((item) => (
                      <tr key={item.terminalFiserv}>
                        <td>{item.terminalFiserv}</td><td>{(item.marcas || []).join(", ") || "-"}</td><td>{item.cantidadOperacionesFiserv || 0}</td><td>{money(item.importeFiserv)}</td><td>{item.estado || "UNMAPPED"}</td><td>{item.cloverCandidate?.merchantId || "-"}</td><td>{item.cloverCandidate?.commerceName || "-"}</td><td>{item.cloverCandidate?.accountKey || "-"}</td><td>{item.confianza || 0}</td>
                        <td>{item.confirmedDurably ? <Button size="sm" variant="success" disabled>Guardado</Button> : item.cloverCandidate ? <Button size="sm" variant="outline-success" onClick={() => confirmTerminalMapping(item)}>Confirmar</Button> : <Button size="sm" variant="outline-secondary" disabled>Buscar otro</Button>}</td>
                      </tr>
                    ))}</tbody>
                  </Table>
                  <small className="text-muted">Confirmar aplica el mapping una vez por terminal, conserva candidatos existentes y no inicia consultas Clover.</small>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        ) : null}
        {debit ? (
          <Card className="daily-card mt-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div><div className="daily-eyebrow">FLUJO ESPECÍFICO</div><h2>CONCILIACIÓN DÉBITO</h2><p>Universo independiente del matcher general.</p></div>
                <Button title="Se consultarán únicamente los reports faltantes de los mappings nuevos." variant="success" onClick={consultarCloverNuevosMappings} disabled={!debit.newReportNeeds}>Consultar Clover para nuevos mappings ({debit.newReportNeeds || 0})</Button>
              </div>
              <div className="daily-summary-grid mt-3">
                <div><small>Golden Fiserv Debit</small><strong>{debit.goldenFiserv}</strong></div><div><small>Disponible Builder</small><strong>{debit.builderAvailable}</strong></div><div><small>Mapeado</small><strong>{debit.mapped} / {debit.builderAvailable}</strong></div><div><small>Sin mapping</small><strong>{debit.unmapped}</strong></div><div><small>Recuperadas vs 67</small><strong>{debit.recoveredFromPrevious}</strong></div>
              </div>
              <p className="mt-3 mb-1">Sin mapping: <strong>{debit.unmapped} operaciones</strong> · Terminales pendientes: <strong>{debit.pendingMappings?.length || 0}</strong>.</p>
              {debit.pendingMappings?.length ? <div className="daily-table-wrap"><Table size="sm" responsive><thead><tr><th>Terminal</th><th>Familias</th><th>Operaciones Débito</th><th>Importe Débito</th><th>Estado</th><th>Merchant</th><th>Cuenta</th><th>Confianza</th><th /></tr></thead><tbody>{debit.pendingMappings.map((item) => <tr key={item.terminal}><td>{item.terminal}</td><td>{(item.familias || []).join(", ")}</td><td>{item.operations}</td><td>{money(item.amount)}</td><td>{item.status}</td><td>{item.candidateMerchant || "-"}</td><td>{item.candidateAccount || "-"}</td><td>{item.confidence}</td><td><Button size="sm" variant="outline-primary" onClick={() => { setManualMapping(item); setMerchantQuery(""); setMerchantCandidates([]); }}>Buscar otro</Button></td></tr>)}</tbody></Table></div> : null}
            </Card.Body>
          </Card>
        ) : null}
        <Modal show={Boolean(manualMapping)} onHide={() => setManualMapping(null)}>
          <Modal.Header closeButton><Modal.Title>Buscar comercio Clover · {manualMapping?.terminal}</Modal.Title></Modal.Header>
          <Modal.Body><Form.Control value={merchantQuery} placeholder="Nombre de comercio o merchantId" onChange={(event) => setMerchantQuery(event.target.value)} />
            <Button className="mt-2" disabled={!merchantQuery.trim()} onClick={async () => setMerchantCandidates(await buscarCloverMerchants(merchantQuery))}>Buscar</Button>
            <div className="mt-3">{merchantCandidates.map((candidate) => <Button key={`${candidate.merchantId}|${candidate.accountKey}`} className="w-100 text-start mb-2" variant="outline-success" onClick={() => { confirmTerminalMapping({ ...manualMapping, cloverCandidate: candidate }); setManualMapping(null); }}><strong>{candidate.commerceName}</strong><br /><small>{candidate.merchantId} · {candidate.accountKey}</small></Button>)}</div>
          </Modal.Body>
        </Modal>
        <Card className="daily-card daily-history-card">
          <Card.Body>
            <div className="daily-card-heading">
              <div>
                <h2>Histórico</h2>
                <p>Conciliaciones ejecutadas recientemente.</p>
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cargarHistorialConciliacion}
                disabled={cargandoHistorial}
              >
                {cargandoHistorial ? "Actualizando..." : "Actualizar"}
              </Button>
            </div>
            <div className="daily-table-wrap">
              <Table responsive hover className="daily-table">
                <thead>
                  <tr>
                    {[
                      "Fecha liquidación",
                      "Fecha ejecución",
                      "Estado",
                      "Operaciones",
                      "Match rate",
                      "Importe conciliado",
                      "Revisión",
                      "Usuario",
                      "",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historialConciliacion?.runs?.length ? (
                    historialConciliacion.runs.map((row) => (
                      <tr key={row.id}>
                        <td>{date(row.fechaLiquidacion)}</td>
                        <td>{date(row.fechaCreacion)}</td>
                        <td>
                          <Badge bg="success">
                            {row.estado || "Procesada"}
                          </Badge>
                        </td>
                        <td>
                          {row.cantidadFiserv ?? row.operacionesFiserv ?? 0}
                        </td>
                        <td>{pct(row.matchAutomaticoPorcentaje)}</td>
                        <td>{money(row.importeConciliado)}</td>
                        <td>{row.requierenRevision ?? row.conflictos ?? 0}</td>
                        <td>{row.usuarioNombre || "-"}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => abrirRunConciliacion(row.id)}
                          >
                            Ver detalle
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="daily-table-empty">
                        {historialConciliacion?.error ||
                          "Todavía no hay conciliaciones persistidas."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
        <Modal
          show={Boolean(mappingModal)}
          onHide={() => setMappingModal(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Administrar mapping</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">
              Terminal: <strong>{mappingModal?.terminalFiserv}</strong>
            </p>
            <div className="daily-mapping-search">
              <Form.Control
                value={mappingQuery || ""}
                onChange={(event) => setMappingQuery(event.target.value)}
                placeholder="Buscar comercio Clover por nombre o merchant"
                onKeyDown={(event) =>
                  event.key === "Enter" && searchMappingCandidates()
                }
              />
              <Button onClick={searchMappingCandidates}>Buscar</Button>
            </div>
            <div className="daily-table-wrap">
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Comercio</th>
                    <th>Merchant</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {mappingCandidates?.length ? (
                    mappingCandidates.map((candidate, index) => (
                      <tr key={candidate.merchantId || index}>
                        <td>{candidate.commerceName || "-"}</td>
                        <td>
                          {candidate.merchantId ||
                            candidate.dashboardMerchantId ||
                            "-"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="success"
                            disabled={mappingBusy}
                            onClick={() => saveMapping(candidate)}
                          >
                            Cambiar
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">
                        Buscá un comercio para cambiar el mapping.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {mappingModal?.cloverCandidate ? (
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => unlinkMapping(mappingModal)}
              >
                Desvincular mapping
              </Button>
            ) : null}
          </Modal.Body>
        </Modal>
        {finalReady ? (
          <Card className="daily-card">
            <Card.Body>
              <div className="daily-card-heading">
                <div>
                  <div className="daily-eyebrow">VALIDACIÓN</div>
                  <h2>Resultados persistidos</h2>
                  <p>
                    Abrí cada resultado para ver los datos Fiserv y Clover
                    usados.
                  </p>
                </div>
              </div>
              <div className="daily-tabs">
                {["MATCH", "REVIEW", "MISSING", "SOLO_CLOVER"].map(
                  (key) => (
                    <button
                      type="button"
                      className={tab === key ? "active" : ""}
                      onClick={() => setTab(key)}
                      key={key}
                    >
                      {key}
                      <span>
                        {
                          (resultadoConciliacion.resultados || []).filter(
                            (row) => resultCategory(row) === key,
                          ).length
                        }
                      </span>
                    </button>
                  ),
                )}
              </div>
              <div className="daily-table-wrap">
                <Table responsive hover className="daily-table">
                  <thead>
                    <tr>
                      <th>Terminal</th>
                      <th>Fecha</th>
                      <th>Importe</th>
                      <th>Resultado</th>
                      <th>Regla</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {resultRows.length ? (
                      resultRows.map((row, index) => (
                        <tr key={index}>
                          <td>
                            {row.fiserv?.terminal ||
                              row.fiserv?.numeroComercio ||
                              "-"}
                          </td>
                          <td>
                            {date(
                              row.fiserv?.fechaOperacion ||
                                row.fiserv?.operationDate,
                            )}
                          </td>
                          <td>
                            {money(
                              row.fiserv?.totalBruto ?? row.fiserv?.amount,
                            )}
                          </td>
                          <td>
                            <Badge
                              bg={
                                resultCategory(row) === "MATCH"
                                  ? "success"
                                  : "warning"
                              }
                            >
                              {resultCategory(row)}
                            </Badge>
                          </td>
                          <td>{row.rule || "-"}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="link"
                              onClick={() => setSelectedResult(row)}
                            >
                              Ver detalle
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="daily-table-empty">
                          No hay resultados en esta categoría.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        ) : null}
        <Modal
          show={Boolean(selectedResult)}
          onHide={() => setSelectedResult(null)}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detalle de validación:{" "}
              {selectedResult ? resultCategory(selectedResult) : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <h5>Fiserv</h5>
                <pre className="small bg-light p-3 rounded">
                  {JSON.stringify(selectedResult?.fiserv || null, null, 2)}
                </pre>
              </div>
              <div className="col-md-6">
                <h5>Clover</h5>
                <pre className="small bg-light p-3 rounded">
                  {JSON.stringify(selectedResult?.clover || null, null, 2)}
                </pre>
              </div>
            </div>
            <h6>Regla y evidencia</h6>
            <pre className="small bg-light p-3 rounded">
              {JSON.stringify(
                {
                  rule: selectedResult?.rule,
                  evidence: selectedResult?.evidence,
                  contradictions: selectedResult?.contradictions,
                  reason: selectedResult?.reason,
                  candidates: selectedResult?.candidates,
                },
                null,
                2,
              )}
            </pre>
            {resultCategory(selectedResult || {}) === "REVIEW" ? <div className="mt-3"><h6>Candidatos Clover</h6><Table responsive size="sm"><thead><tr><th>PaymentId</th><th>Importe</th><th>Last4</th><th>Receipt</th><th>Batch</th><th>Terminal</th><th>Fecha</th></tr></thead><tbody>{(selectedResult?.candidates || []).map((candidate) => <tr key={candidate.paymentId}><td>{candidate.paymentId}</td><td>{money(candidate.amount)}</td><td>{candidate.cardLast4}</td><td>{candidate.receiptNumber}</td><td>{candidate.batchNumber}</td><td>{candidate.detailTerminal || candidate.terminal}</td><td>{date(candidate.operationDate)}</td></tr>)}</tbody></Table></div> : null}
            {resultCategory(selectedResult || {}) === "REVIEW" ? (
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={() => resolverReview(selectedResult, "PENDING")}>Dejar pendiente</Button>
                <Button variant="outline-danger" onClick={() => resolverReview(selectedResult, "MANUAL_MISSING")}>Marcar sin correspondencia</Button>
                {(selectedResult?.candidates || []).map((candidate) => <Button key={candidate.paymentId} variant="success" onClick={() => resolverReview(selectedResult, "MANUAL_MATCH", candidate.paymentId)}>Confirmar {candidate.paymentId}</Button>)}
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
        {operationalRun ? (
          <Alert
            variant={matchInputError ? "danger" : "light"}
            className="small"
          >
            <strong>Diagnóstico de run:</strong> CRM Run completo:{" "}
            {operationalRun.reconciliationRunId || "-"} · Clover runs:{" "}
            {(operationalRun.cloverRunIds || []).join(", ") || "-"} · Matcher
            status: {operationalRun.status || "-"}
            {matchInputError ? (
              <>
                <br />
                No se pudo ejecutar la validación de operaciones:{" "}
                {matchInputError}
              </>
            ) : null}
          </Alert>
        ) : null}
      </div>
    </section>
  );
}
